import React from 'react';
import { Plus, Loader2, AlertTriangle, Sparkles } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ROLE_LABELS } from '../adminAccessMatrix';

const STAFF_ROLES = ['license_manager', 'email_manager', 'technical_expert', 'support_executive'];

/**
 * Two-stage Add-Staff dialog. Parent owns ALL state (email/name/role/pw/otp
 * + stage + submitting) so the dialog can be reset/replayed cleanly between
 * opens. The split into a child keeps the parent under 250 LOC.
 */
export function AddStaffDialog({
  open, onOpenChange,
  stage,
  email, setEmail,
  name, setName,
  role, setRole,
  initialPw, setInitialPw,
  emailOtp, setEmailOtp,
  waOtp, setWaOtp,
  otpInfo,
  submitting,
  onSuggestPassword,
  onInitiate,
  onVerify,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="team-add-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Plus className="w-4 h-4 text-amber-400" /> Add admin staff
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {stage === 'details'
              ? "Fill in the staff details. We'll dispatch OTPs for you to confirm."
              : 'Enter both 6-digit codes to finalise the creation.'}
          </DialogDescription>
        </DialogHeader>

        {stage === 'details' ? (
          <div className="space-y-3">
            <Input
              type="email"
              placeholder="staff@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="team-add-email"
              className="bg-slate-800/50 border-slate-700 text-white"
            />
            <Input
              placeholder="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="team-add-name"
              className="bg-slate-800/50 border-slate-700 text-white"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="team-add-role"
              className="w-full bg-slate-800/50 border border-slate-700 text-white rounded-md px-3 py-2 text-sm"
            >
              {STAFF_ROLES.map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <Input
                placeholder="Initial password (≥8 chars)"
                value={initialPw}
                onChange={(e) => setInitialPw(e.target.value)}
                data-testid="team-add-password"
                className="bg-slate-800/50 border-slate-700 text-white font-mono"
              />
              <Button
                onClick={onSuggestPassword}
                variant="outline"
                size="sm"
                type="button"
                data-testid="team-suggest-password"
                className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Suggest
              </Button>
            </div>
            <div className="text-[11px] text-slate-400">
              The staff member will be forced to change this password on first login.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <OtpInfoCard info={otpInfo} />
            <Input
              placeholder="Email OTP (6 digits)"
              value={emailOtp}
              onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              data-testid="team-add-email-otp"
              className="bg-slate-800/50 border-slate-700 text-white font-mono tracking-widest text-center"
            />
            <Input
              placeholder="WhatsApp OTP (6 digits)"
              value={waOtp}
              onChange={(e) => setWaOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              data-testid="team-add-wa-otp"
              className="bg-slate-800/50 border-slate-700 text-white font-mono tracking-widest text-center"
            />
          </div>
        )}

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} variant="outline" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Cancel</Button>
          {stage === 'details' ? (
            <Button onClick={onInitiate} disabled={submitting} data-testid="team-add-initiate" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Send OTPs
            </Button>
          ) : (
            <Button onClick={onVerify} disabled={submitting} data-testid="team-add-verify" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirm create
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OtpInfoCard({ info }) {
  if (!info) return null;
  return (
    <div className="rounded-md bg-slate-800/60 border border-slate-700 p-3 text-xs space-y-1">
      <div className="text-slate-400">Email OTP sent to <span className="text-slate-200">{info.email_sent_to}</span></div>
      <div className="text-slate-400">WhatsApp OTP sent to <span className="text-slate-200">{info.whatsapp_sent_to}</span></div>
      {info.whatsapp_is_mocked && (
        <div className="mt-2 text-amber-300 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> WhatsApp is mocked — use <code className="font-mono">{info.whatsapp_mock_code_hint}</code>
        </div>
      )}
    </div>
  );
}

export default AddStaffDialog;
